import { useEffect, useRef, useCallback } from 'react'
import TransitionUtils, { TRANSITION_CLASSES } from '../utils/transitionUtils'

interface UseTransitionsOptions {
  autoInitialize?: boolean
  enableScrollReveal?: boolean
  enableStagger?: boolean
}

export const useTransitions = (options: UseTransitionsOptions = {}): any => {
  const {
    autoInitialize = true,
    enableScrollReveal = true,
    enableStagger = true
  } = options

  const containerRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Initialize transitions for the container
  const initializeTransitions = useCallback((): void => {
    if (containerRef.current === null) return

    const container = containerRef.current

    // Enhance all buttons
    const buttons = container.querySelectorAll('button, .ant-btn')
    buttons.forEach((button) => {
      TransitionUtils.enhanceButton(button as HTMLElement)
    })

    // Enhance all cards
    const cards = container.querySelectorAll('.ant-card, .card-border')
    cards.forEach((card) => {
      TransitionUtils.enhanceCard(card as HTMLElement)
    })

    // Enhance all form inputs
    const inputs = container.querySelectorAll('input, textarea, select')
    inputs.forEach((input) => {
      TransitionUtils.enhanceFormInput(input as HTMLElement)
    })

    // Enhance all table rows
    const tableRows = container.querySelectorAll('tbody tr')
    tableRows.forEach((row) => {
      TransitionUtils.enhanceTableRow(row as HTMLElement)
    })

    // Enhance all modals
    const modals = container.querySelectorAll('.ant-modal')
    modals.forEach((modal) => {
      TransitionUtils.createModalTransition(modal as HTMLElement)
    })

    // Enhance all dropdowns
    const dropdowns = container.querySelectorAll('.ant-select-dropdown')
    dropdowns.forEach((dropdown) => {
      TransitionUtils.enhanceDropdown(dropdown as HTMLElement)
    })

    // Enhance all tabs
    const tabs = container.querySelectorAll('.ant-tabs')
    tabs.forEach((tabsContainer) => {
      TransitionUtils.enhanceTabs(tabsContainer as HTMLElement)
    })

    // Enhance all navigation items
    const navItems = container.querySelectorAll('.ant-menu-item, .nav-item')
    navItems.forEach((navItem) => {
      TransitionUtils.enhanceNavigation(navItem as HTMLElement)
    })

    // Enhance all sidebars
    const sidebars = container.querySelectorAll('.ant-layout-sider, .sidebar')
    sidebars.forEach((sidebar) => {
      TransitionUtils.enhanceSidebar(sidebar as HTMLElement)
    })

    // Enhance all progress bars
    const progressBars = container.querySelectorAll('.ant-progress')
    progressBars.forEach((progress) => {
      TransitionUtils.enhanceProgress(progress as HTMLElement)
    })

    // Enhance all tooltips
    const tooltips = container.querySelectorAll('.ant-tooltip')
    tooltips.forEach((tooltip) => {
      TransitionUtils.enhanceTooltip(tooltip as HTMLElement)
    })

    // Apply stagger animations if enabled
    if (enableStagger) {
      const staggerContainers = container.querySelectorAll('.stagger-container')
      staggerContainers.forEach((staggerContainer) => {
        TransitionUtils.applyStaggerAnimation(staggerContainer as HTMLElement)
      })
    }

    // Initialize scroll reveal if enabled
    if (enableScrollReveal) {
      const scrollRevealElements = container.querySelectorAll('.scroll-reveal')
      if (scrollRevealElements.length > 0) {
        observerRef.current = TransitionUtils.createScrollReveal(scrollRevealElements)
      }
    }
  }, [enableScrollReveal, enableStagger])

  // Cleanup function
  const cleanup = useCallback((): void => {
    if (observerRef.current !== null) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
  }, [])

  // Auto-initialize on mount if enabled
  useEffect(() => {
    if (autoInitialize) {
      initializeTransitions()
    }

    return cleanup
  }, [autoInitialize, initializeTransitions, cleanup])

  // Enhanced transition methods
  const showLoading = useCallback((element: HTMLElement, text?: string): void => {
    TransitionUtils.createLoadingSkeleton(element, text)
  }, [])

  const hideLoading = useCallback((element: HTMLElement): void => {
    TransitionUtils.removeLoadingSkeleton(element)
  }, [])

  const showError = useCallback((element: HTMLElement): void => {
    TransitionUtils.showError(element)
  }, [])

  const showSuccess = useCallback((element: HTMLElement): void => {
    TransitionUtils.showSuccess(element)
  }, [])

  const showNotification = useCallback((element: HTMLElement): void => {
    TransitionUtils.showNotification(element)
  }, [])

  const applyTransitionClass = useCallback((element: HTMLElement, className: string): void => {
    element.classList.add(className)
  }, [])

  const removeTransitionClass = useCallback((element: HTMLElement, className: string): void => {
    element.classList.remove(className)
  }, [])

  const createPageTransition = useCallback(async (
    element: HTMLElement,
    direction: 'enter' | 'exit' = 'enter',
    duration?: number
  ) => {
    await TransitionUtils.createPageTransition(element, direction, duration)
  }, [])

  const applyStaggerAnimation = useCallback((container: HTMLElement, childSelector?: string): void => {
    TransitionUtils.applyStaggerAnimation(container, childSelector)
  }, [])

  const createScrollReveal = useCallback((elements: NodeListOf<Element> | Element[], config?: any) => {
    return TransitionUtils.createScrollReveal(elements, config)
  }, [])

  const enhanceElement = useCallback((element: HTMLElement, type: 'button' | 'card' | 'input' | 'tableRow'): void => {
    switch (type) {
      case 'button':
        TransitionUtils.enhanceButton(element)
        break
      case 'card':
        TransitionUtils.enhanceCard(element)
        break
      case 'input':
        TransitionUtils.enhanceFormInput(element)
        break
      case 'tableRow':
        TransitionUtils.enhanceTableRow(element)
        break
    }
  }, [])

  const debounce = useCallback(<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ) => {
    return TransitionUtils.debounce(func, wait)
  }, [])

  const throttle = useCallback(<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ) => {
    return TransitionUtils.throttle(func, limit)
  }, [])

  const prefersReducedMotion = useCallback((): boolean => {
    return TransitionUtils.prefersReducedMotion()
  }, [])

  const applyIfMotionAllowed = useCallback((
    element: HTMLElement,
    classes: string[]
  ): void => {
    TransitionUtils.applyIfMotionAllowed(element, classes)
  }, [])

  return {
    // Refs
    containerRef,

    // Methods
    initializeTransitions,
    cleanup,
    showLoading,
    hideLoading,
    showError,
    showSuccess,
    showNotification,
    applyTransitionClass,
    removeTransitionClass,
    createPageTransition,
    applyStaggerAnimation,
    createScrollReveal,
    enhanceElement,
    debounce,
    throttle,
    prefersReducedMotion,
    applyIfMotionAllowed,

    // Constants
    TRANSITION_CLASSES
  }
}

export default useTransitions
